import { useEffect, useRef, useState } from 'react'
import { bridge } from '../../bridge'
import type { Product } from '../../bridge/types'
import { useAppStore } from '../../store/app'
import { submitProSubscription } from '../../api/client'
import { sendRedeemOffer } from '../../api/ws'
import './PaywallSheet.css'

const PRODUCT_PRO = 'pro_monthly_v2'
const PRODUCT_MULTI = 'pro_multi_home_monthly_v2'

type Tier = typeof PRODUCT_PRO | typeof PRODUCT_MULTI

export function PaywallSheet({ upgradeOnly = false }: { upgradeOnly?: boolean }) {
  const isSubscribed = useAppStore((s) => s.isSubscribed)
  const hasActiveOffer = useAppStore((s) => s.hasActiveOffer)
  const organizationID = useAppStore((s) => s.organizationID) ?? ''
  const availableOrganizations = useAppStore((s) => s.availableOrganizations)
  const popSheet = useAppStore((s) => s.popSheet)

  const ownedOrgCount = availableOrganizations.filter((o) => o.role === 'owner').length
  // Pro single-home covers all owned orgs; allowing it when the user owns >1
  // would be a multi-home loophole. Block the tier entirely in that case.
  const proSingleBlocked = ownedOrgCount > 1

  const currentOrg = availableOrganizations.find((o) => o.is_current)
  const isOrgPro = currentOrg?.is_pro ?? false
  // In upgrade mode the org is already Pro (via pro_monthly) — suppress the
  // "unclaimed" hint and always show the footer so the user can upgrade.
  const appleSubUnclaimedHere = !upgradeOnly && isSubscribed && !isOrgPro
  const showFooter = upgradeOnly || !isOrgPro

  const [proProduct, setProProduct] = useState<Product | null>(null)
  const [multiProduct, setMultiProduct] = useState<Product | null>(null)
  // In upgrade mode, or when Pro single is blocked, default to multi-home.
  const [selectedTier, setSelectedTier] = useState<Tier>(
    upgradeOnly || proSingleBlocked ? PRODUCT_MULTI : PRODUCT_PRO,
  )
  const [purchasing, setPurchasing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [offerJustRedeemed, setOfferJustRedeemed] = useState(false)
  const [purchaseClaimed, setPurchaseClaimed] = useState(false)

  useEffect(() => {
    bridge
      .call('storekit.products', { productIDs: [PRODUCT_PRO, PRODUCT_MULTI] })
      .then((r) => {
        setProProduct(r.products.find((p) => p.id === PRODUCT_PRO) ?? null)
        setMultiProduct(r.products.find((p) => p.id === PRODUCT_MULTI) ?? null)
      })
      .catch(() => {})
  }, [])

  // Capture offer state at mount so we don't pop for offers that were already
  // active when the sheet opened (server pushes offer_status on every connect).
  const offerActiveAtMount = useRef(hasActiveOffer)

  useEffect(() => {
    if (!hasActiveOffer || offerActiveAtMount.current || offerJustRedeemed) return
    setOfferJustRedeemed(true)
    setTimeout(() => popSheet(), 1500)
  }, [hasActiveOffer, offerJustRedeemed, popSheet])

  // Same treatment for a purchase: once the server has claimed the transaction,
  // show the confirmation for a beat and then dismiss. Keyed off the claim
  // succeeding rather than off is_pro, so it works for an upgrade too (where
  // the org is already Pro at mount and is_pro never transitions).
  useEffect(() => {
    if (!purchaseClaimed) return
    const t = setTimeout(() => popSheet(), 1500)
    return () => clearTimeout(t)
  }, [purchaseClaimed, popSheet])

  async function purchase() {
    setPurchasing(true)
    setError(null)
    try {
      const r = await bridge.call('storekit.purchase', { productID: selectedTier })
      if (r.outcome === 'failed') {
        setError(r.error ?? 'Purchase failed')
      } else if (r.outcome === 'success') {
        await submitLatestTransaction()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setPurchasing(false)
    }
  }

  async function restore() {
    setError(null)
    try {
      await bridge.call('storekit.restore')
      await submitLatestTransaction()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function submitLatestTransaction() {
    if (!organizationID) {
      setError('No household selected — cannot claim subscription.')
      return
    }
    try {
      const { jws, productId } = await bridge.call('storekit.latestTransactionJWS')
      if (!jws) {
        setError(
          bridge.isAndroid
            ? 'No verified Play purchase available to submit.'
            : 'No verified Apple transaction available to submit. If you are testing with an Xcode StoreKit Configuration, switch to Sandbox (Edit Scheme → Run → Options → StoreKit Configuration: None) and sign into a Sandbox Apple ID under Settings → App Store.',
        )
        return
      }
      await submitProSubscription({
        signedTransaction: jws,
        organizationID,
        platform: bridge.isAndroid ? 'google' : 'apple',
        productId,
      })
      // Server has verified the transaction and claimed it to this user; it
      // also pushed a fresh organizations_list, so is_pro is already flipping
      // underneath us. Confirm, then dismiss.
      setPurchaseClaimed(true)
    } catch (err) {
      setError(
        'Subscribed locally, but server claim failed: ' +
          (err instanceof Error ? err.message : String(err)),
      )
    }
  }

  const proPrice = proProduct?.priceFormatted ?? '$2.99'
  const multiPrice = multiProduct?.priceFormatted ?? '$9.99'
  const selectedPrice = selectedTier === PRODUCT_PRO ? proPrice : multiPrice

  return (
    <div className="paywall">
      <div className="paywall__scroll">
        <div className="paywall__logo" aria-hidden>🔔</div>
        <div className="paywall__title">Hearth Pro</div>
        <div className="paywall__subtitle">
          {upgradeOnly ? 'Upgrade to Multi-Home' : 'Advanced control for power users'}
        </div>

        {(isOrgPro || purchaseClaimed) && (
          <div className="paywall__subscribed">
            {purchaseClaimed
              ? '✓ You\'re subscribed!'
              : upgradeOnly
                ? '✓ You already have a Pro subscription'
                : '✓ You\'re subscribed!'}
          </div>
        )}

        {!upgradeOnly && !isOrgPro && !appleSubUnclaimedHere && (
          <div className="paywall__tiers">
            <TierCard
              label="Pro"
              price={proPrice}
              detail="1 household"
              selected={bridge.isNative && !proSingleBlocked && selectedTier === PRODUCT_PRO}
              onSelect={bridge.isNative && !proSingleBlocked ? () => setSelectedTier(PRODUCT_PRO) : undefined}
              blocked={proSingleBlocked}
              blockedNote={`You own ${ownedOrgCount} households`}
            />
            <TierCard
              label="Multi-Home"
              price={multiPrice}
              detail="Up to 10 households"
              selected={bridge.isNative && selectedTier === PRODUCT_MULTI}
              onSelect={bridge.isNative ? () => setSelectedTier(PRODUCT_MULTI) : undefined}
            />
          </div>
        )}

        <div className="paywall__section">
          <div className="paywall__section-label paywall__section-label--pro">PRO</div>
          <Feature
            icon="🔔"
            title="Push notifications"
            subtitle="Works even when the app is closed"
            color="var(--success)"
          />
          <Feature
            icon="⏳"
            title="Temporary grants"
            subtitle="Set expirations on approval rules"
            color="var(--success)"
          />
        </div>

        <div className="paywall__divider" />

        <div className="paywall__section">
          <div className="paywall__section-label">FREE</div>
          <Feature
            icon="💬"
            title="Send commands to your agents"
            subtitle="Send text to your agent from your phone"
            color="var(--label-secondary)"
          />
          <Feature
            icon="✓"
            title="Approve, deny, or halt"
            subtitle="Full control with a single tap"
            color="var(--label-secondary)"
          />
          <Feature
            icon="〜"
            title="Live activity stream"
            subtitle="Watch your agents work in real time"
            color="var(--label-secondary)"
          />
        </div>
      </div>

      {showFooter && (
        <div className="paywall__footer">
          {!bridge.isNative ? (
            <WebOfferSection upgradeOnly={upgradeOnly} />
          ) : appleSubUnclaimedHere ? (
            <>
              <div className="paywall__fine">
                Apple subscription detected on this device but not claimed for
                this household. Claim it here, or sign in to the original
                household to use it there.
              </div>
              <button
                className="paywall__subscribe"
                disabled={purchasing}
                onClick={async () => {
                  setPurchasing(true)
                  setError(null)
                  try {
                    await submitLatestTransaction()
                  } finally {
                    setPurchasing(false)
                  }
                }}
              >
                {purchasing ? '…' : 'Claim for this household'}
              </button>
            </>
          ) : (
            <button
              className="paywall__subscribe"
              disabled={purchasing}
              onClick={purchase}
            >
              {purchasing ? '…' : (
                <>
                  <span className="paywall__subscribe-price">
                    {upgradeOnly ? 'Upgrade to Multi-Home' : 'Subscribe'} — {selectedPrice}/month
                  </span>
                  {!upgradeOnly && selectedTier === PRODUCT_MULTI && (
                    <span className="paywall__subscribe-detail">Up to 10 households</span>
                  )}
                </>
              )}
            </button>
          )}

          {error && <div className="paywall__error">{error}</div>}

          {!appleSubUnclaimedHere && bridge.isNative && (
            <div className="paywall__fine">
              {upgradeOnly || selectedTier === PRODUCT_MULTI
                ? 'Covers up to 10 households. Cancel anytime.'
                : 'This subscription covers one household only. Cancel anytime.'}
            </div>
          )}

          <div className="paywall__links">
            <a
              href="https://hearthcmd.com/privacy.html"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            <a
              href="https://hearthcmd.com/terms.html"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </a>
            {bridge.isNative && (
              <button className="paywall__linklike" onClick={restore}>
                Restore Purchases
              </button>
            )}
          </div>

          {offerJustRedeemed && (
            <div className="paywall__unlocked">
              <div className="paywall__unlocked-icon">✓</div>
              <div className="paywall__unlocked-text">Pro unlocked!</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function WebOfferSection({ upgradeOnly }: { upgradeOnly: boolean }) {
  const hasActiveOffer = useAppStore((s) => s.hasActiveOffer)
  const offerError = useAppStore((s) => s.offerError)
  const setActiveOffer = useAppStore((s) => s.setActiveOffer)
  const setOfferError = useAppStore((s) => s.setOfferError)
  // Offer users upgrading: hide the "subscribe in the app" message — they
  // can only move tiers via a new offer code (closed-loop, no App Store upsell).
  const subscribedPlatform = useAppStore((s) => s.subscribedPlatform)
  const hideNativeCTA = upgradeOnly && subscribedPlatform === 'offer'

  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const waitingRef = useRef(false)

  // Resolve when server echoes offer_status back.
  useEffect(() => {
    if (!waitingRef.current) return
    if (hasActiveOffer) { waitingRef.current = false; setLoading(false); setCode('') }
  }, [hasActiveOffer])

  useEffect(() => {
    if (!waitingRef.current) return
    if (offerError) { waitingRef.current = false; setLoading(false) }
  }, [offerError])

  function submit() {
    const trimmed = code.trim()
    if (!trimmed || loading) return
    setActiveOffer(false)
    setOfferError(null)
    waitingRef.current = true
    setLoading(true)
    sendRedeemOffer(trimmed)
  }

  return (
    <div className="paywall__web-offer">
      {!hideNativeCTA && (
        <div className="paywall__fine">
          {upgradeOnly
            ? 'To upgrade, open the Hearth app on your iPhone or Android device.'
            : 'To subscribe, open the Hearth app on your iPhone or Android device.'}
        </div>
      )}
      {(!upgradeOnly || hideNativeCTA) && (
        <>
          <div className="paywall__web-offer-divider">
            {upgradeOnly ? 'enter a multi-home offer code' : 'or enter an offer code'}
          </div>
          <div className="paywall__web-offer-row">
            <input
              className="paywall__web-offer-input"
              type="text"
              placeholder="Offer code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              disabled={loading}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
            />
            <button
              className="paywall__web-offer-btn"
              disabled={!code.trim() || loading}
              onClick={submit}
            >
              {loading ? '…' : 'Redeem'}
            </button>
          </div>
          {hasActiveOffer && !loading && (
            <div className="paywall__web-offer-success">✓ Offer code active</div>
          )}
          {offerError && !loading && (
            <div className="paywall__error">{offerError}</div>
          )}
        </>
      )}
    </div>
  )
}

function TierCard({
  label,
  price,
  detail,
  selected,
  onSelect,
  blocked,
  blockedNote,
}: {
  label: string
  price: string
  detail: string
  selected: boolean
  onSelect?: () => void
  blocked?: boolean
  blockedNote?: string
}) {
  const content = (
    <>
      <div className="paywall__tier-label">{label}</div>
      <div className="paywall__tier-price">{price}<span className="paywall__tier-period">/mo</span></div>
      <div className="paywall__tier-detail">{blocked && blockedNote ? blockedNote : detail}</div>
    </>
  )
  if (!onSelect) {
    return (
      <div className={`paywall__tier paywall__tier--static${blocked ? ' paywall__tier--blocked' : ''}`}>
        {content}
      </div>
    )
  }
  return (
    <button
      className={`paywall__tier${selected ? ' paywall__tier--selected' : ''}`}
      onClick={onSelect}
    >
      {content}
    </button>
  )
}

function Feature({
  icon,
  title,
  subtitle,
  color,
}: {
  icon: string
  title: string
  subtitle: string
  color: string
}) {
  return (
    <div className="paywall__feature">
      <div className="paywall__feature-icon" style={{ color }}>
        {icon}
      </div>
      <div className="paywall__feature-body">
        <div className="paywall__feature-title">{title}</div>
        <div className="paywall__feature-subtitle">{subtitle}</div>
      </div>
    </div>
  )
}
